/**
 * JsonRecord — a JSON RESULT read as a labelled record instead of as source.
 *
 * ── WHAT THIS IS FOR ─────────────────────────────────────────────────────────────────────────
 *
 * `{"satisfied": true, "acceptance_met": [...], "gaps": []}` is not code a reader is editing, it
 * is the agent's answer. Rendered through the code card it arrives as syntax-highlighted
 * punctuation with a horizontal scrollbar; the reader has to parse it to learn the one thing they
 * came for. This renders the same document as fields: a label, and the value in the form that
 * value actually has.
 *
 * ── IT IS NOT A SECOND CODE BLOCK ────────────────────────────────────────────────────────────
 *
 * It renders INSIDE `ChatCodeBlock`'s existing card, in place of the highlighted body, so the
 * header, the collapse control, `Open`, `Copy` and `Download` are all unchanged and still act on
 * the RAW document. Nothing here is a new container, a new set of actions, or a second way to
 * open something. Whether a block reaches this at all is `services/multi-agent/jsonView`'s
 * decision, and it declines for anything it cannot lay out honestly.
 *
 * ── THE RULES IT RENDERS BY ──────────────────────────────────────────────────────────────────
 *
 *   · NO HORIZONTAL SCROLL. Every value wraps. A long string becomes a paragraph under its label
 *     rather than a line that runs off the edge — which was half of what made the raw block hard
 *     to read in a 700px column.
 *   · COLOUR IS NEVER THE ONLY CARRIER (§9). A boolean is a glyph AND the word Yes/No; the tick
 *     is `aria-hidden` and the word is what a screen reader gets.
 *   · ONE VOCABULARY FOR NOTHING. `null`, `[]`, `{}` and `""` all render as "None", because a
 *     reader does not care which spelling of absent the producer chose. The raw document still
 *     carries the distinction for anyone who copies it.
 *   · IT NEVER CLAIMS TO SHOW EVERYTHING. Past `PREVIEW_FIELDS` the rest are counted, not hidden
 *     silently, and the card's own `Open` is what shows the whole document.
 */
import { Check, Minus, X } from "lucide-react";
import type { FieldShape, JsonRecordView, JsonScalar, ViewField } from "@/services/multi-agent/jsonView";

/** A scalar inside a list — the one place a value renders without its own label. */
function scalarText(value: JsonScalar): string {
  if (value === null) return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function BooleanValue({ value }: { value: boolean }) {
  const Icon = value ? Check : X;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[13px] font-medium ${
        value ? "text-[var(--ma-state-ok-text)]" : "text-[var(--ma-state-err-text)]"
      }`}
    >
      <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
      {value ? "Yes" : "No"}
    </span>
  );
}

function EmptyValue() {
  /*
   * "None", not an em dash and not `[]`.
   *
   * §1 of the frontend rules reserves the em dash for a value the producer did not REPORT. An
   * empty array is reported, and it is the answer — "gaps: []" means there were no gaps, which is
   * usually the good news in the document. Printing it as a dash would read as missing data.
   */
  return (
    <span className="inline-flex items-center gap-1 text-[13px] text-muted-foreground">
      <Minus aria-hidden className="h-3 w-3 shrink-0" />
      None
    </span>
  );
}

function ShapeValue({ shape, depth }: { shape: FieldShape; depth: number }) {
  switch (shape.kind) {
    case "boolean":
      return <BooleanValue value={shape.value} />;
    case "number":
      return <span className="text-[13px] tabular-nums text-foreground">{shape.value}</span>;
    case "text":
      return <span className="text-[13px] text-foreground">{shape.value}</span>;
    case "empty":
      return <EmptyValue />;
    case "paragraph":
      // `whitespace-pre-wrap` so an embedded newline is kept, `break-words` so a long identifier
      // cannot push the card wider than the column.
      return (
        <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground">
          {shape.value}
        </p>
      );
    case "list":
      return (
        <ul className="flex list-none flex-col gap-1">
          {shape.items.map((item, index) => (
            <li key={index} className="flex items-start gap-1.5 text-[13px] text-foreground">
              <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
              <span className="min-w-0 break-words">{scalarText(item)}</span>
            </li>
          ))}
        </ul>
      );
    case "object":
      return <FieldList fields={shape.fields} depth={depth + 1} />;
    case "raw":
      /*
       * The shapes this view will not lay out — an array of objects, or something nested past the
       * depth cap. It shows the compact JSON rather than a placeholder, because "3 items" with no
       * sight of them is strictly less than the code block the reader had before.
       */
      return (
        <div className="flex flex-col gap-0.5">
          {shape.count !== null && (
            <span className="text-[11px] text-muted-foreground">
              {shape.count} {shape.count === 1 ? "entry" : "entries"}
            </span>
          )}
          <code className="break-all rounded bg-muted/50 px-1.5 py-1 font-mono text-[12px] text-muted-foreground">
            {shape.json}
          </code>
        </div>
      );
  }
}

/**
 * Is this value short enough to sit BESIDE its label?
 *
 * Two layouts, chosen per field rather than one compromise for all: a scalar reads as
 * `Label            value` on one line, which is what makes a record scannable down its left
 * edge; anything with body — a list, a paragraph, a nested object — gets the full width under its
 * label, which is what stops it wrapping into a narrow right-hand column.
 */
function isInline(shape: FieldShape): boolean {
  return shape.kind === "boolean" || shape.kind === "number" || shape.kind === "text" || shape.kind === "empty";
}

function FieldList({ fields, depth }: { fields: readonly ViewField[]; depth: number }) {
  return (
    <dl className={`flex min-w-0 flex-col ${depth > 0 ? "gap-1.5" : "gap-2.5"}`}>
      {fields.map((f) => {
        const inline = isInline(f.shape);
        return (
          <div
            key={f.key}
            data-json-field={f.key}
            className={
              inline
                ? "flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"
                : "flex min-w-0 flex-col gap-1"
            }
          >
            <dt
              className="shrink-0 text-[12px] font-medium text-muted-foreground"
              title={f.key === f.label ? undefined : f.key}
            >
              {f.label}
            </dt>
            {/* `min-w-0` so a long value can shrink and wrap instead of widening the row (§8.3). */}
            <dd className={`min-w-0 ${inline ? "text-right" : "ps-3"}`}>
              <ShapeValue shape={f.shape} depth={depth} />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function JsonRecord({ view }: { view: JsonRecordView }) {
  return (
    <div data-testid="json-record" className="min-w-0 px-3 py-2.5">
      <FieldList fields={view.fields} depth={0} />
      {view.hiddenCount > 0 && (
        <p className="mt-2.5 border-t border-border/60 pt-2 text-[11px] text-muted-foreground">
          {view.hiddenCount} more {view.hiddenCount === 1 ? "field" : "fields"} — open or copy for the
          whole document.
        </p>
      )}
    </div>
  );
}

export default JsonRecord;
